import { sendMetric, getWhatId } from "@utils";
import React, { useEffect, useMemo, useRef, useState } from "react";

import Ava from "./Ava";

import Fortune, { FORTUNE_WORD_COUNT } from "./Fortune";

import "./style.scss";

import { inject, observer } from "mobx-react";
import Category from "./Category";
import { getScaledTimings } from "./timings";
const { VITE_WHAT_ID } = import.meta.env;

const AIModal = inject("mainStore")(
    observer(({ mainStore }) => {
        const init = mainStore.getMainStore("init");
        const settingLego = mainStore.getMainStore("settingLego");
        const wdCd = mainStore.getMainStore("codeTypeMetrika");
        const abTestMetika =  mainStore.getMainStore("abTestMetika");
        

        const [isFortune, setIsFortune] = useState(false);
        const [progressPercent, setProgressPercent] = useState(0);
        const progressBarRef = useRef(null);
        const progressStartRef = useRef(0);
        const progressRafRef = useRef(null);

        const isAi = init.data._w_d._cd.startsWith("ai");
        const Specified = isAi ? Ava : Category;
        const what_id = getWhatId() ? getWhatId() : VITE_WHAT_ID;

        const handleClick = () => {
            if (window.risMetrika && typeof window.risMetrika.reachgoal_ch === "function") {
                window.risMetrika.reachgoal_ch({
                    event: "widget_step_2_click",
                    siteId: what_id,
                    session_uuid: localStorage.getItem("_r_p_s"),
                    widget_code: wdCd ? wdCd : "",
                });
                sendMetric(`widget_step_2_click`, {
                    params1: [`${abTestMetika ? abTestMetika : "ris_promo"}`],
                    params2: null,
                    params3: null,
                    params4: null,
                    params6: null,
                    params7: wdCd ? wdCd : null,
                    params8: what_id,
                });
            }
            mainStore.setMainStore("isStart", false);
            mainStore.setMainStore("isCoverRoot", false);
            mainStore.setMainStore("modal", false);
        };

        const queueDurationSec = Number(settingLego?.queueDuration);
        const queueDurationMs = Number.isFinite(queueDurationSec) ? queueDurationSec * 1000 : null;
        const timings = useMemo(() => getScaledTimings({ queueDurationMs, isAi, fortuneWordCount: FORTUNE_WORD_COUNT }), [queueDurationMs, isAi]);
        const progressDurationMs = timings.totalMs;
        const cssTiming = timings?.css;
        const timingVars = cssTiming
            ? {
                  "--category-title-exit-ms": `${cssTiming.categoryTitleExitMs}ms`,
                  "--category-track-exit-ms": `${cssTiming.categoryTrackExitMs}ms`,
                  "--category-show-ms": `${cssTiming.categoryShowMs}ms`,
                  "--category-show-delay-1-ms": `${cssTiming.categoryShowDelay1Ms}ms`,
                  "--category-show-delay-2-ms": `${cssTiming.categoryShowDelay2Ms}ms`,
                  "--category-show-delay-3-ms": `${cssTiming.categoryShowDelay3Ms}ms`,
                  "--category-show-delay-4-ms": `${cssTiming.categoryShowDelay4Ms}ms`,
                  "--category-item-transition-ms": `${cssTiming.categoryItemTransitionMs}ms`,
                  "--ava-item-in-ms": `${cssTiming.avaItemInMs}ms`,
                  "--ava-item-in-delay-1-ms": `${cssTiming.avaItemInDelay1Ms}ms`,
                  "--ava-item-in-delay-2-ms": `${cssTiming.avaItemInDelay2Ms}ms`,
                  "--ava-title-exit-ms": `${cssTiming.avaTitleExitMs}ms`,
                  "--ava-shift-ms": `${cssTiming.avaShiftMs}ms`,
                  "--ava-category-exit-ms": `${cssTiming.avaCategoryExitMs}ms`,
                  "--ava-category-enter-ms": `${cssTiming.avaCategoryEnterMs}ms`,
                  "--fortune-title-word-ms": `${cssTiming.fortuneTitleWordMs}ms`,
                  "--fortune-track-intro-ms": `${cssTiming.fortuneTrackIntroMs}ms`,
                  "--fortune-idle-opacity-ms": `${cssTiming.fortuneIdleOpacityMs}ms`,
                  "--fortune-spin-ms": `${cssTiming.fortuneSpinMs}ms`,
                  "--fortune-settle-ms": `${cssTiming.fortuneSettleMs}ms`,
                  "--fortune-prize-highlight-ms": `${cssTiming.fortunePrizeHighlightMs}ms`,
                  "--fortune-prize-highlight-delay-ms": `${cssTiming.fortunePrizeHighlightDelayMs}ms`,
              }
            : {};

        useEffect(() => {
            if (!Number.isFinite(progressDurationMs) || progressDurationMs <= 0) {
                setProgressPercent(100);
                if (progressBarRef.current) {
                    progressBarRef.current.style.setProperty("--progress", "1");
                }
                return;
            }

            setProgressPercent(0);
            const start = performance.now();
            progressStartRef.current = start;

            const tick = (now) => {
                const elapsed = now - start;
                const progress = Math.min(1, elapsed / progressDurationMs);
                if (progressBarRef.current) {
                    progressBarRef.current.style.setProperty("--progress", `${progress}`);
                }

                if (elapsed < progressDurationMs) {
                    progressRafRef.current = requestAnimationFrame(tick);
                }
            };

            progressRafRef.current = requestAnimationFrame(tick);

            return () => {
                if (progressRafRef.current) {
                    cancelAnimationFrame(progressRafRef.current);
                }
            };
        }, [progressDurationMs]);

        useEffect(() => {
            if (!Number.isFinite(progressDurationMs) || progressDurationMs <= 0) {
                setProgressPercent(100);
                return;
            }

            const startTime = progressStartRef.current || performance.now();
            const intervalId = setInterval(() => {
                const elapsed = performance.now() - startTime;
                const nextPercent = Math.min(100, Math.round((elapsed / progressDurationMs) * 100));

                setProgressPercent((prev) => (prev === nextPercent ? prev : nextPercent));

                if (elapsed >= progressDurationMs) {
                    clearInterval(intervalId);
                }
            }, 100);

            return () => clearInterval(intervalId);
        }, [progressDurationMs]);

        const isPaletteBg = settingLego?.bgColor === "palette";

        const modalBgColor = isPaletteBg ? "var(--color2)" : "var(--color5)";
        const ctaColor = settingLego?.ctaColor === "palette" ? "var(--color7)" : "#19b677";

        return (
            <div className={`ai-modal`} style={{ "--bg-color": modalBgColor, ...timingVars, "--cta-color": ctaColor }}>
                {!isPaletteBg && <div className="ai-modal__top"></div>}
                {isFortune ? <Fortune timings={timings} /> : <Specified setIsFortune={setIsFortune} timings={timings} isFortune={isFortune} />}
                {!isPaletteBg && (
                    <svg width="1280" height="491" viewBox="0 0 1280 491" fill="none" xmlns="http://www.w3.org/2000/svg" className="ai-modal__hue" draggable="false">
                        <g filter="url(#filter0_f_7927_32654)">
                            <ellipse cx="640.443" cy="490.797" rx="467.443" ry="302.463" fill="var(--color1)" />
                        </g>
                        <defs>
                            <filter id="filter0_f_7927_32654" x="-15.3333" y="0.000167847" width="1311.55" height="981.593" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                                <feGaussianBlur stdDeviation="94.1667" result="effect1_foregroundBlur_7927_32654" />
                            </filter>
                        </defs>
                    </svg>
                )}
                <div className="ai-modal__left"></div>
                <div className="ai-modal__right"></div>
                <div className="ai-modal__progress">
                    <div
                        ref={progressBarRef}
                        className={`ai-modal__progress-bar ${progressPercent >= 100 ? "is-complete" : ""}`}
                        onClick={() => {
                            handleClick();
                        }}
                    >
                        <span className="ai-modal__progress-text">Выбрать подарок</span>
                    </div>
                    {progressPercent !== 100 && <span className="ai-modal__progress-percent">{progressPercent}%</span>}
                </div>
            </div>
        );
    }),
);

export default AIModal;
